<template>
  <section class="dd-gift-redeem">
    <div class="dd-wall-card dd-gift-redeem__card">
      <div class="dd-gift-redeem__icon" aria-hidden="true">
        <img :src="giftIcon" alt="" />
      </div>

      <h2 class="dd-gift-redeem__title">{{ $t('wall.subscription.gift_redeem_title') }}</h2>
      <p v-if="!authenticated" class="dd-gift-redeem__subtitle">{{ $t('wall.subscription.gift_redeem_subtitle') }}</p>
      <p class="dd-gift-redeem__note">{{ $t('wall.subscription.gift_note_90_days') }}</p>

      <div v-if="!token" class="dd-gift-redeem__status dd-gift-redeem__status--error">
        {{ $t('wall.subscription.gift_invalid_link') }}
      </div>

      <div v-else-if="!authenticated" class="dd-gift-redeem__actions">
        <SigninWithGoogle :href="loginHref" />
      </div>

      <div v-else-if="loading" class="dd-gift-redeem__status">
        {{ $t('wall.subscription.gift_redeeming') }}
      </div>

      <template v-else>
        <div v-if="confirmRequired" class="dd-gift-redeem__status dd-gift-redeem__status--warning">
          {{ $t('wall.subscription.gift_replace_warning', { current: currentLevelName, gift: previewLevelName || '-' }) }}
        </div>
        <div v-else-if="success" class="dd-gift-redeem__status dd-gift-redeem__status--success">
          {{ $t('wall.subscription.gift_redeemed_success') }}
        </div>
        <div v-else-if="errorText" class="dd-gift-redeem__status dd-gift-redeem__status--error">
          {{ errorText }}
        </div>

        <div class="dd-gift-redeem__actions">
          <button
            v-if="confirmRequired && token"
            class="btn"
            type="button"
            :disabled="loading"
            @click="redeem"
          >
            {{ $t('wall.subscription.gift_activate_anyway') }}
          </button>
          <button
            v-else-if="!success && token"
            class="btn btn--outline"
            type="button"
            :disabled="loading"
            @click="loadPreviewAndMaybeRedeem"
          >
            {{ $t('wall.subscription.gift_redeem_retry') }}
          </button>
          <router-link class="btn-orange" to="/wall/subscription">
            {{ $t('wall.subscription.gift_go_subscription') }}
          </router-link>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import SigninWithGoogle from '@/new/components/general/SigninWithGoogle.vue';
import giftIcon from '@/assets/img/gift-fill.svg';
import SubscriptionStatus from '@/store/models/SubscriptionStatus';
import { logWarn } from '@/helpers';

export default {
  name: 'WallGiftRedeem',
  components: { SigninWithGoogle },
  data() {
    return {
      giftIcon,
      loading: false,
      success: false,
      errorText: '',
      confirmRequired: false,
      previewLevelName: '',
    };
  },
  computed: {
    authenticated() {
      return this.$store.getters.authenticated;
    },
    token() {
      return (this.$route.params.token || '').trim();
    },
    loginHref() {
      return `/login?provider=google&redirect=${encodeURIComponent(this.$route.fullPath)}`;
    },
    currentSubscription() {
      return this.$store.getters.currentSubscription;
    },
    hasPaidSubscription() {
      return this.currentSubscription?.status === SubscriptionStatus.Paid;
    },
    currentLevelName() {
      return this.currentSubscription?.level?.name || '-';
    },
  },
  methods: {
    async loadPreviewAndMaybeRedeem() {
      if (!this.authenticated || !this.token || this.loading) {
        return;
      }
      this.loading = true;
      this.errorText = '';
      this.confirmRequired = false;
      this.previewLevelName = '';
      try {
        const preview = await this.$store.dispatch('previewGiftSubscriptionRedeem', this.token);
        if (!preview || !preview.success) {
          this.success = false;
          this.errorText = preview?.error_text || this.$t('error.error_while_subscribing', { code: '-' });
          return;
        }
        this.previewLevelName = preview.gift_level_name || '';
        if (preview.gift_link?.is_activated) {
          this.success = true;
          return;
        }
        if (this.hasPaidSubscription && preview.will_replace_current_subscription) {
          this.confirmRequired = true;
          return;
        }
        await this.redeem();
      } catch (error) {
        this.success = false;
        this.errorText = error?.response?.data?.error_text
          || this.$t('error.error_while_subscribing', { code: error?.response?.status || '-' });
        logWarn('Gift redeem preview failed', error);
      } finally {
        this.loading = false;
      }
    },
    async redeem() {
      if (!this.token) {
        return;
      }
      this.loading = true;
      this.errorText = '';
      try {
        const response = await this.$store.dispatch('redeemGiftSubscription', this.token);
        if (response && response.success) {
          this.success = true;
          this.confirmRequired = false;
          this.previewLevelName = response.gift_level_name || this.previewLevelName;
          this.$store.dispatch('updateSubscription');
          return;
        }
        this.success = false;
        this.errorText = response && response.error_text
          ? response.error_text
          : this.$t('error.error_while_subscribing', { code: '-' });
      } catch (error) {
        this.success = false;
        this.errorText = error?.response?.data?.error_text
          || this.$t('error.error_while_subscribing', { code: error?.response?.status || '-' });
        logWarn('Gift redeem failed', error);
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loadPreviewAndMaybeRedeem();
  },
  watch: {
    authenticated() {
      this.loadPreviewAndMaybeRedeem();
    },
    '$route.params.token'() {
      this.loading = false;
      this.success = false;
      this.errorText = '';
      this.confirmRequired = false;
      this.previewLevelName = '';
      this.loadPreviewAndMaybeRedeem();
    },
  },
};
</script>

<style scoped>
.dd-gift-redeem {
  margin-top: 24px;
  display: flex;
  justify-content: center;
}
.dd-gift-redeem__card {
  width: 100%;
  max-width: 640px;
}
.dd-gift-redeem__icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: #fff2df;
  color: var(--dd-accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.dd-gift-redeem__icon img {
  width: 32px;
  height: 32px;
}
.dd-gift-redeem__title {
  margin: 12px 0 0;
}
.dd-gift-redeem__subtitle {
  margin: 10px 0 0;
}
.dd-gift-redeem__note {
  margin: 10px 0 0;
  color: #666;
}
.dd-gift-redeem__status {
  margin-top: 16px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f5f5f5;
}
.dd-gift-redeem__status--success {
  background: #eaf8ea;
  color: #1f6a1f;
}
.dd-gift-redeem__status--warning {
  background: #fff7e6;
  color: #8a5a00;
}
.dd-gift-redeem__status--error {
  background: #fee9e9;
  color: #8a1f1f;
}
.dd-gift-redeem__actions {
  margin-top: 16px;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}
</style>
